import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Target, BarChart3, Loader2, Plus, X, ArrowRight,
  TrendingUp, AlertTriangle, Shield, Trophy, Eye, Zap, Globe,
  FileText, MapPin, Sparkles, CheckCircle2, XCircle, Copy,
  ChevronDown, ChevronUp, ArrowUpRight, ExternalLink, Clock, ListChecks,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { api, useAuth } from '../../context/AuthContext';
import { useProject } from '../../context/ProjectContext';
import { useTaskState } from '../../context/TaskStateContext';
import { toast } from 'sonner';

// FastAPI `detail` may be a plain string or a structured object (e.g. package-gate
// errors: { error, message, feature, required, current }). Always coerce to a
// renderable string so it never reaches JSX/toast as an object.
const detailMessage = (detail, fallback = null) => {
  if (detail && typeof detail === 'object') return detail.message || fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const CompetitorAnalysisPage = () => {
  const { user } = useAuth();
  const { selectedProjectId, selectedProject } = useProject();
  // Clients own a single site (the selected project's domain), so we never ask
  // them for "Your Website URL" — we auto-use the project domain and only ask
  // for the competitor site(s). Admins/PMs still see the field.
  const isClient = user?.role === 'client';
  const projectDomain = selectedProject?.domain || '';
  // Business name is prefilled from the project (the info we already have) and
  // the backend falls back to fetching it from the website when left blank.
  const [businessName, setBusinessName] = useState('');
  const [activeTab, setActiveTab] = useState('content-gap');
  const contentGapTask = useTaskState('competitor-content-gap');
  const comparisonTask = useTaskState('competitor-site-comparison');
  const keywordGapTask = useTaskState('competitor-keyword-gap');
  const webIntelTask = useTaskState('competitor-web-intel');
  const loading = contentGapTask.running || comparisonTask.running || keywordGapTask.running || webIntelTask.running;
  const [analyses, setAnalyses] = useState([]);
  const [contentGapResult, setContentGapResult] = useState(null);
  const [comparisonResult, setComparisonResult] = useState(null);
  const [keywordGapResult, setKeywordGapResult] = useState(null);
  const [webIntelResult, setWebIntelResult] = useState(null);

  // Restore results from TaskStateContext when navigating back to this page
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (contentGapTask.result) setContentGapResult(contentGapTask.result); }, [contentGapTask.result]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (comparisonTask.result) setComparisonResult(comparisonTask.result); }, [comparisonTask.result]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (keywordGapTask.result) setKeywordGapResult(keywordGapTask.result); }, [keywordGapTask.result]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (webIntelTask.result) setWebIntelResult(webIntelTask.result); }, [webIntelTask.result]);

  const [contentGapForm, setContentGapForm] = useState({ competitor_urls: ['', '', ''], service: '', zip_code: '', gmb_link: '' });
  const [comparisonForm, setComparisonForm] = useState({ my_url: '', competitor_urls: ['', '', ''], service: '', zip_code: '', gmb_link: '' });
  const [keywordGapForm, setKeywordGapForm] = useState({ my_url: '', competitor_url: '', zip_code: '' });
  const [webIntelForm, setWebIntelForm] = useState({ my_url: '', competitor_url: '', service: '' });

  const [myRequests, setMyRequests] = useState({ items: [], loading: false, loaded: false });
  const [myRequestsStatusFilter, setMyRequestsStatusFilter] = useState('');

  const navigate = useNavigate();

  useEffect(() => { fetchAnalyses(); }, []);

  // Prefill business name from the project, and (for clients) auto-fill their
  // own website URL with the project domain so they only need competitor sites.
  useEffect(() => {
    setBusinessName(selectedProject?.name || '');
    if (isClient && projectDomain) {
      setComparisonForm(p => ({ ...p, my_url: projectDomain }));
      setKeywordGapForm(p => ({ ...p, my_url: projectDomain }));
      setWebIntelForm(p => ({ ...p, my_url: projectDomain }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject]);

  const fetchAnalyses = async () => {
    try {
      const resp = await api.get('/competitor/analyses');
      setAnalyses(resp.data);
    } catch { /* ignore */ }
  };

  const fetchMyRequests = async () => {
    if (!selectedProjectId) return;
    setMyRequests(prev => ({ ...prev, loading: true }));
    try {
      const resp = await api.get('/competitor/replicate/status', {
        params: { project_id: selectedProjectId, page_size: 100 },
      });
      setMyRequests({ items: resp.data?.items || [], loading: false, loaded: true });
    } catch {
      setMyRequests(prev => ({ ...prev, loading: false, loaded: true }));
    }
  };

  const runContentGap = async () => {
    const urls = contentGapForm.competitor_urls.filter(u => u.trim());
    if (urls.length === 0) { toast.error('Add at least one competitor URL'); return; }
    contentGapTask.start();
    try {
      const resp = await api.post('/competitor/content-analysis', {
        project_id: selectedProjectId || null,
        competitor_urls: urls,
        service: contentGapForm.service,
        zip_code: contentGapForm.zip_code,
        gmb_link: contentGapForm.gmb_link,
        business_name: businessName,
      });
      setContentGapResult(resp.data);
      toast.success('Content gap analysis complete');
      contentGapTask.complete(resp.data);
      fetchAnalyses();
    } catch (err) {
      const msg = detailMessage(err.response?.data?.detail) || 'Analysis failed';
      toast.error(msg);
      contentGapTask.fail(msg);
    }
  };

  const runComparison = async () => {
    const myUrl = isClient ? projectDomain : comparisonForm.my_url.trim();
    if (!myUrl) { toast.error(isClient ? 'No website set for this project' : 'Enter your website URL'); return; }
    const urls = comparisonForm.competitor_urls.filter(u => u.trim());
    if (urls.length === 0) { toast.error('Add at least one competitor URL'); return; }
    comparisonTask.start();
    try {
      const resp = await api.post('/competitor/site-comparison', {
        project_id: selectedProjectId || null,
        my_url: myUrl,
        competitor_urls: urls,
        service: comparisonForm.service,
        zip_code: comparisonForm.zip_code,
        gmb_link: comparisonForm.gmb_link,
        business_name: businessName,
      });
      setComparisonResult(resp.data);
      toast.success('Site comparison complete');
      comparisonTask.complete(resp.data);
      fetchAnalyses();
    } catch (err) {
      const msg = detailMessage(err.response?.data?.detail) || 'Comparison failed';
      toast.error(msg);
      comparisonTask.fail(msg);
    }
  };

  const runKeywordGap = async () => {
    const myUrl = isClient ? projectDomain : keywordGapForm.my_url.trim();
    if (!myUrl) { toast.error(isClient ? 'No website set for this project' : 'Enter your website URL'); return; }
    if (!keywordGapForm.competitor_url.trim()) { toast.error('Enter the competitor URL'); return; }
    keywordGapTask.start();
    try {
      const resp = await api.post('/competitor/keyword-gap', {
        project_id: selectedProjectId || null,
        my_url: myUrl,
        competitor_url: keywordGapForm.competitor_url,
        zip_code: keywordGapForm.zip_code,
        business_name: businessName,
      });
      setKeywordGapResult(resp.data);
      toast.success('Keyword gap analysis complete');
      keywordGapTask.complete(resp.data);
      fetchAnalyses();
    } catch (err) {
      const msg = detailMessage(err.response?.data?.detail) || 'Analysis failed';
      toast.error(msg);
      keywordGapTask.fail(msg);
    }
  };

  const runWebIntel = async () => {
    const myUrl = isClient ? projectDomain : webIntelForm.my_url.trim();
    if (!myUrl) { toast.error(isClient ? 'No website set for this project' : 'Enter your website URL'); return; }
    if (!webIntelForm.competitor_url.trim()) { toast.error('Enter the competitor URL'); return; }
    webIntelTask.start();
    try {
      const resp = await api.post('/competitor/web-intel', {
        project_id: selectedProjectId || null,
        my_url: myUrl,
        competitor_url: webIntelForm.competitor_url,
        service: webIntelForm.service,
        business_name: businessName,
      });
      setWebIntelResult(resp.data);
      toast.success('Web intel analysis complete');
      webIntelTask.complete(resp.data);
      fetchAnalyses();
    } catch (err) {
      const msg = detailMessage(err.response?.data?.detail) || 'Web intel analysis failed';
      toast.error(msg);
      webIntelTask.fail(msg);
    }
  };

  const loadPastAnalysis = (a) => {
    const data = {
      ...a.result,
      analysis_id: a.analysis_id,
      my_url: a.my_url || a.result?.my_url,
      competitor_url: a.competitor_url || a.result?.competitor_url,
    };
    if (a.analysis_type === 'content_gap') {
      setContentGapResult(data);
      setActiveTab('content-gap');
    } else if (a.analysis_type === 'site_comparison') {
      setComparisonResult(data);
      setActiveTab('comparison');
    } else if (a.analysis_type === 'keyword_gap') {
      setKeywordGapResult(data);
      setActiveTab('keyword-gap');
    } else if (a.analysis_type === 'web_intel') {
      setWebIntelResult(data);
      setActiveTab('web-intel');
    }
  };

  const deleteAnalysis = async (e, analysisId) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this analysis?')) return;
    try {
      await api.delete(`/competitor/analyses/${analysisId}`);
      toast.success('Analysis deleted');
      setAnalyses(prev => prev.filter(a => a.analysis_id !== analysisId));
    } catch {
      toast.error('Failed to delete analysis');
    }
  };

  const updateUrlField = (form, setForm, index, value) => {
    const urls = [...form.competitor_urls];
    urls[index] = value;
    setForm(prev => ({ ...prev, competitor_urls: urls }));
  };

  // Shared Business Name input — prefilled from the project, editable, sent as
  // analysis context (backend fetches it from the website when left blank).
  const businessNameField = (
    <div>
      <Label>Business Name <span className="text-slate-400 font-normal">(used as context)</span></Label>
      <Input placeholder="e.g. Acme Plumbing" value={businessName}
        onChange={e => setBusinessName(e.target.value)} />
    </div>
  );

  // For clients we already know their website — show it instead of asking.
  const clientSiteNote = isClient ? (
    <div className="flex items-center gap-2 text-sm rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 px-3 py-2">
      <Globe className="h-4 w-4 text-indigo-500 flex-shrink-0" />
      <span className="text-slate-500 dark:text-slate-400">
        Your site:{' '}
        <span className="font-medium text-slate-700 dark:text-slate-300">
          {projectDomain || 'no website set for this project'}
        </span>
      </span>
    </div>
  ) : null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Competitor Analysis</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1">
          Analyse competitor sites, find content gaps, compare signals, and discover keyword opportunities
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="content-gap" className="flex items-center gap-1.5">
            <Search className="h-3.5 w-3.5" /> Content Gaps
          </TabsTrigger>
          <TabsTrigger value="comparison" className="flex items-center gap-1.5">
            <BarChart3 className="h-3.5 w-3.5" /> Site Comparison
          </TabsTrigger>
          <TabsTrigger value="keyword-gap" className="flex items-center gap-1.5">
            <Target className="h-3.5 w-3.5" /> Keyword Gap
          </TabsTrigger>
          <TabsTrigger value="web-intel" className="flex items-center gap-1.5">
            <Globe className="h-3.5 w-3.5" /> Web Intel
          </TabsTrigger>
          <TabsTrigger value="my-requests" className="flex items-center gap-1.5">
            <ListChecks className="h-3.5 w-3.5" /> My Requests
          </TabsTrigger>
        </TabsList>

        {/* ── Content Gap Analysis (Prompt #1) ──────────────────── */}
        <TabsContent value="content-gap" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Search className="h-5 w-5 text-indigo-500" /> Competitor Content Gap Analysis</CardTitle>
              <CardDescription>Crawl competitor sites and find content topics, trust signals, and FAQs they're missing</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {businessNameField}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Service / Industry</Label>
                  <Input placeholder="e.g. Plumbing" value={contentGapForm.service} onChange={e => setContentGapForm(p => ({ ...p, service: e.target.value }))} />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input placeholder="e.g. 78701" value={contentGapForm.zip_code} onChange={e => setContentGapForm(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Google Business Profile Link <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input placeholder="e.g. https://g.co/kgs/abc123 or Google Maps URL" value={contentGapForm.gmb_link} onChange={e => setContentGapForm(p => ({ ...p, gmb_link: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Competitor URLs</Label>
                {contentGapForm.competitor_urls.map((url, i) => (
                  <Input key={i} placeholder={`Competitor ${i + 1} URL`} value={url}
                    onChange={e => updateUrlField(contentGapForm, setContentGapForm, i, e.target.value)} />
                ))}
              </div>
              <Button onClick={runContentGap} disabled={loading} className="w-full sm:w-auto">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analysing...</> : <><Sparkles className="h-4 w-4 mr-2" /> Run Content Gap Analysis</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Site Comparison (Prompt #4) ───────────────────────── */}
        <TabsContent value="comparison" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-emerald-500" /> Site vs Competitor Comparison</CardTitle>
              <CardDescription>Compare services, locations, pricing signals, and trust signals side-by-side</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {isClient ? clientSiteNote : (
                <div>
                  <Label>Your Website URL</Label>
                  <Input placeholder="https://yoursite.com" value={comparisonForm.my_url}
                    onChange={e => setComparisonForm(p => ({ ...p, my_url: e.target.value }))} />
                </div>
              )}
              {businessNameField}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Service / Industry</Label>
                  <Input placeholder="e.g. HVAC Repair" value={comparisonForm.service} onChange={e => setComparisonForm(p => ({ ...p, service: e.target.value }))} />
                </div>
                <div>
                  <Label>Zip Code</Label>
                  <Input placeholder="e.g. 75201" value={comparisonForm.zip_code} onChange={e => setComparisonForm(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Google Business Profile Link <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input placeholder="e.g. https://g.co/kgs/abc123 or Google Maps URL" value={comparisonForm.gmb_link} onChange={e => setComparisonForm(p => ({ ...p, gmb_link: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label>Competitor URLs</Label>
                {comparisonForm.competitor_urls.map((url, i) => (
                  <Input key={i} placeholder={`Competitor ${i + 1} URL`} value={url}
                    onChange={e => updateUrlField(comparisonForm, setComparisonForm, i, e.target.value)} />
                ))}
              </div>
              <Button onClick={runComparison} disabled={loading} className="w-full sm:w-auto">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Comparing...</> : <><Zap className="h-4 w-4 mr-2" /> Run Site Comparison</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Keyword Gap (Prompt #7) ──────────────────────────── */}
        <TabsContent value="keyword-gap" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-orange-500" /> Competitor Keyword Gap</CardTitle>
              <CardDescription>Find keywords your competitor targets that you don't have pages for</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {clientSiteNote}
              {businessNameField}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {!isClient && (
                  <div>
                    <Label>Your Website URL</Label>
                    <Input placeholder="https://yoursite.com" value={keywordGapForm.my_url}
                      onChange={e => setKeywordGapForm(p => ({ ...p, my_url: e.target.value }))} />
                  </div>
                )}
                <div>
                  <Label>Competitor Website URL</Label>
                  <Input placeholder="https://competitor.com" value={keywordGapForm.competitor_url}
                    onChange={e => setKeywordGapForm(p => ({ ...p, competitor_url: e.target.value }))} />
                </div>
                <div>
                  <Label>Zip Code <span className="text-slate-400 font-normal">(optional)</span></Label>
                  <Input placeholder="e.g. 90210" value={keywordGapForm.zip_code}
                    onChange={e => setKeywordGapForm(p => ({ ...p, zip_code: e.target.value }))} />
                </div>
              </div>
              <Button onClick={runKeywordGap} disabled={loading} className="w-full sm:w-auto">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analysing...</> : <><Target className="h-4 w-4 mr-2" /> Run Keyword Gap Analysis</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Web Intel (Deep Side-by-Side) ───────────────────── */}
        <TabsContent value="web-intel" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2"><Globe className="h-5 w-5 text-violet-500" /> Competitor Web Intel</CardTitle>
              <CardDescription>Deep side-by-side analysis — crawl both sites and get actionable win points you can replicate</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {clientSiteNote}
              {businessNameField}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!isClient && (
                  <div>
                    <Label>Your Website URL</Label>
                    <Input placeholder="https://yoursite.com" value={webIntelForm.my_url}
                      onChange={e => setWebIntelForm(p => ({ ...p, my_url: e.target.value }))} />
                  </div>
                )}
                <div>
                  <Label>Competitor Website URL</Label>
                  <Input placeholder="https://competitor.com" value={webIntelForm.competitor_url}
                    onChange={e => setWebIntelForm(p => ({ ...p, competitor_url: e.target.value }))} />
                </div>
              </div>
              <div>
                <Label>Service / Industry <span className="text-slate-400 font-normal">(optional)</span></Label>
                <Input placeholder="e.g. Digital Marketing" value={webIntelForm.service}
                  onChange={e => setWebIntelForm(p => ({ ...p, service: e.target.value }))} />
              </div>
              <Button onClick={runWebIntel} disabled={loading} className="w-full sm:w-auto">
                {webIntelTask.running ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Analysing both sites...</> : <><Globe className="h-4 w-4 mr-2" /> Run Web Intel Analysis</>}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── My Replicate Requests ─────────────────────────────── */}
        <TabsContent value="my-requests" className="space-y-4 mt-4">
          {!selectedProjectId ? (
            <Card>
              <CardContent className="p-8 text-center text-slate-500">
                Select a project to view your replicate requests.
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <ListChecks className="h-5 w-5 text-indigo-500" /> My Replicate Requests
                    </CardTitle>
                    <CardDescription>All replicate requests submitted for this project</CardDescription>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={myRequestsStatusFilter}
                      onChange={e => setMyRequestsStatusFilter(e.target.value)}
                      className="text-xs border border-slate-200 dark:border-slate-700 rounded-md px-2 py-1.5 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300"
                    >
                      <option value="">All statuses</option>
                      {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" onClick={fetchMyRequests} disabled={myRequests.loading}>
                      {myRequests.loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Refresh'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {!myRequests.loaded ? (
                  <div className="py-12 text-center">
                    <Button onClick={fetchMyRequests} disabled={myRequests.loading}>
                      {myRequests.loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading...</> : 'Load Requests'}
                    </Button>
                  </div>
                ) : myRequests.items.length === 0 ? (
                  <p className="py-8 text-center text-slate-400 text-sm">No replicate requests yet for this project.</p>
                ) : (
                  <div className="space-y-2">
                    {myRequests.items
                      .filter(r => !myRequestsStatusFilter || r.status === myRequestsStatusFilter)
                      .map(row => {
                        const cfg = STATUS_CONFIG[row.status] || { label: row.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                        const dest = ['pending_approval', 'awaiting_final_approval'].includes(row.status)
                          ? '/dashboard/approvals'
                          : row.task_id ? '/dashboard/tasks' : '/dashboard/approvals';
                        return (
                          <div
                            key={row.replicate_id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-slate-200 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors"
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-900 dark:text-white truncate">{row.title}</p>
                              <p className="text-[11px] text-slate-400 truncate">{row.competitor_url}</p>
                            </div>
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium flex-shrink-0 ${cfg.cls}`}>
                              <Clock className="h-2.5 w-2.5" /> {cfg.label}
                            </span>
                            <span className="text-[11px] text-slate-400 flex-shrink-0 hidden sm:block">
                              {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}
                            </span>
                            <button
                              onClick={() => navigate(dest)}
                              className="flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 font-medium flex-shrink-0"
                            >
                              <ExternalLink className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        );
                      })
                    }
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* ── Results Panel — each tab shows its own result ──────── */}
      <AnimatePresence mode="wait">
        {activeTab === 'content-gap' && contentGapResult && (
          <motion.div key="cg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <ContentGapResults data={contentGapResult} />
          </motion.div>
        )}
        {activeTab === 'comparison' && comparisonResult && (
          <motion.div key="sc" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <SiteComparisonResults data={comparisonResult} />
          </motion.div>
        )}
        {activeTab === 'keyword-gap' && keywordGapResult && (
          <motion.div key="kg" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <KeywordGapResults data={keywordGapResult} />
          </motion.div>
        )}
        {activeTab === 'web-intel' && webIntelResult && (
          <motion.div key="wi" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
            <WebIntelResults data={webIntelResult} selectedProjectId={selectedProjectId} />
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Past Analyses ────────────────────────────────────────── */}
      {analyses.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Past Analyses</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {analyses.slice(0, 10).map(a => (
                <div key={a.analysis_id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800"
                  onClick={() => loadPastAnalysis(a)}>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{a.analysis_type.replace('_', ' ')}</Badge>
                    <span className="text-sm text-slate-600 dark:text-slate-300">
                      {a.competitor_urls?.join(', ') || a.competitor_url || a.my_url}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()}</span>
                    <button
                      onClick={(e) => deleteAnalysis(e, a.analysis_id)}
                      className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 text-slate-400 hover:text-red-500 transition"
                      title="Delete analysis"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


const ContentGapResults = ({ data }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-500" /> Top Content Gaps</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {(data.content_gaps || []).map((gap, i) => {
            const item = typeof gap === 'string' ? { gap_topic: gap } : gap;
            return (
              <div key={i} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                <p className="font-medium text-slate-900 dark:text-white">{item.gap_topic || item.topic || item.title || item.content_gap || Object.values(item)[0]}</p>
                <div className="flex gap-2 mt-1 flex-wrap">
                  {item.search_intent && <Badge variant="outline">{item.search_intent}</Badge>}
                  {item.competitor && <Badge variant="secondary">{item.competitor}</Badge>}
                </div>
                {item.which_competitors_miss_it && <p className="text-xs text-slate-500 mt-1">Missing from: {Array.isArray(item.which_competitors_miss_it) ? item.which_competitors_miss_it.join(', ') : item.which_competitors_miss_it}</p>}
                {item.opportunity && <p className="text-sm text-slate-500 mt-1">{item.opportunity}</p>}
                {item.description && <p className="text-sm text-slate-500 mt-1">{item.description}</p>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-indigo-500" /> Topics to Publish</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-3">
          {(data.topics_to_publish || []).map((t, i) => {
            const item = typeof t === 'string' ? { topic: t } : t;
            return (
              <div key={i} className="p-4 rounded-lg border border-slate-200 dark:border-slate-700">
                <h4 className="font-semibold text-slate-900 dark:text-white">{item.topic || item.title || item.page_title || Object.values(item)[0]}</h4>
                <div className="flex gap-2 mt-1.5 flex-wrap">
                  {item.search_intent && <Badge variant="secondary">{item.search_intent}</Badge>}
                  {item.priority && <Badge variant="outline">{item.priority}</Badge>}
                  {item.estimated_difficulty && <Badge variant="outline">{item.estimated_difficulty}</Badge>}
                </div>
                {item.why_publish && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2">{item.why_publish}</p>}
                {item.content_angle && <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{item.content_angle}</p>}
                {item.target_keyword && <p className="text-sm mt-1"><span className="text-slate-500">Target keyword:</span> <span className="font-medium text-indigo-600">{item.target_keyword}</span></p>}
                {item.suggested_url && <p className="text-sm mt-1"><span className="text-slate-500">Suggested URL:</span> <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{item.suggested_url}</code></p>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Shield className="h-5 w-5 text-red-500" /> Trust Gaps</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-2">
          {(data.trust_gaps || []).map((tg, i) => {
            const item = typeof tg === 'string' ? { detail: tg } : tg;
            return (
              <div key={i} className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                <p className="font-medium text-red-800 dark:text-red-300">
                  {item.competitor || item.site || 'Competitor'}: {item.gap_type || item.detail || item.trust_gap || item.issue || Object.values(item).slice(1).join(' — ')}
                </p>
                {item.recommendation && <p className="text-sm text-red-700 dark:text-red-400 mt-1">{item.recommendation}</p>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  </div>
);


const SiteComparisonResults = ({ data }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><BarChart3 className="h-5 w-5 text-emerald-500" /> Comparison Table</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">Site</th>
                <th className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">Services</th>
                <th className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">Locations</th>
                <th className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">Pricing</th>
                <th className="text-left p-2 font-medium text-slate-600 dark:text-slate-300">Trust Signals</th>
              </tr>
            </thead>
            <tbody>
              {(data.comparison_table || []).map((row, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-2 font-medium text-indigo-600 dark:text-indigo-400 max-w-[150px] truncate">{row.site_url}</td>
                  <td className="p-2">{(row.services || []).join(', ') || '—'}</td>
                  <td className="p-2">{(row.locations || []).join(', ') || '—'}</td>
                  <td className="p-2">{row.pricing_signals || '—'}</td>
                  <td className="p-2">{(row.trust_signals || []).join(', ') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card>
        <CardHeader><CardTitle className="text-red-600 dark:text-red-400 flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Weaknesses</CardTitle></CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {(data.weaknesses || []).map((w, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"><span className="text-red-500 mt-0.5">•</span> {w}</li>
            ))}
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle className="text-emerald-600 dark:text-emerald-400 flex items-center gap-2"><Trophy className="h-4 w-4" /> Your Advantage</CardTitle></CardHeader>
        <CardContent><p className="text-sm">{data.advantage || 'No clear advantage identified'}</p></CardContent>
      </Card>
    </div>
  </div>
);


const KeywordGapResults = ({ data }) => (
  <div className="space-y-4">
    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-blue-500" /> Competitor Top Pages</CardTitle></CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 dark:border-slate-700">
                <th className="text-left p-2">Page URL</th>
                <th className="text-left p-2">Primary Keyword</th>
                <th className="text-left p-2">Est. Traffic</th>
                <th className="text-left p-2">Difficulty</th>
                <th className="text-left p-2">Intent</th>
              </tr>
            </thead>
            <tbody>
              {(data.competitor_top_pages || []).slice(0, 20).map((p, i) => (
                <tr key={i} className="border-b border-slate-100 dark:border-slate-800">
                  <td className="p-2 max-w-[200px] truncate text-indigo-600 dark:text-indigo-400">{p.url}</td>
                  <td className="p-2 font-medium">{p.primary_keyword}</td>
                  <td className="p-2">{p.estimated_monthly_traffic}</td>
                  <td className="p-2"><Badge variant="outline">{p.keyword_difficulty}</Badge></td>
                  <td className="p-2"><Badge variant="secondary">{p.search_intent}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>

    {(data.keyword_gaps || []).length > 0 && (
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Target className="h-5 w-5 text-red-500" /> Keyword Gaps</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2">
            {(data.keyword_gaps || []).slice(0, 15).map((g, i) => (
              <div key={i} className="p-3 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-900 dark:text-white">{g.keyword || g.primary_keyword || g.topic || (typeof g === 'string' ? g : Object.values(g)[0])}</p>
                  <div className="flex gap-2">
                    {g.search_intent && <Badge variant="secondary">{g.search_intent}</Badge>}
                    {g.estimated_difficulty && <Badge variant="outline">{g.estimated_difficulty}</Badge>}
                    {g.traffic_potential && <Badge className="bg-blue-100 text-blue-700">{g.traffic_potential}</Badge>}
                  </div>
                </div>
                {g.reason && <p className="text-sm text-slate-500 mt-1">{g.reason}</p>}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    )}

    <Card>
      <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> Priority Pages to Build</CardTitle></CardHeader>
      <CardContent>
        <div className="space-y-4">
          {(data.priority_pages_to_build || []).map((p, i) => {
            const item = typeof p === 'string' ? { title: p } : p;
            return (
              <div key={i} className="p-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="font-semibold text-slate-900 dark:text-white">
                    {item.title || item.keyword || item.topic || item.page_title || `Page ${i + 1}`}
                  </h4>
                  <div className="flex gap-1.5 flex-shrink-0">
                    {(item.estimated_difficulty || item.difficulty) && (
                      <Badge variant="outline">{item.estimated_difficulty || item.difficulty} difficulty</Badge>
                    )}
                    {item.intent && <Badge variant="secondary">{item.intent}</Badge>}
                  </div>
                </div>
                {item.target_keyword && (
                  <p className="text-sm"><span className="text-slate-500">Target keyword:</span> <span className="font-medium text-indigo-600 dark:text-indigo-400">{item.target_keyword}</span></p>
                )}
                {item.suggested_url_slug && (
                  <p className="text-sm"><span className="text-slate-500">Suggested URL:</span> <code className="text-xs bg-slate-100 dark:bg-slate-800 px-1.5 py-0.5 rounded">{item.suggested_url_slug}</code></p>
                )}
                {item.reason && (
                  <p className="text-sm text-slate-600 dark:text-slate-400">{item.reason}</p>
                )}
                {item.quick_content_brief && (
                  <div className="mt-2 p-3 bg-white dark:bg-slate-900 rounded-md border border-amber-100 dark:border-amber-900">
                    <p className="text-xs font-medium text-slate-500 mb-1">Content Brief</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300">{item.quick_content_brief}</p>
                  </div>
                )}
                {item.conversion_reason && (
                  <p className="text-xs text-amber-700 dark:text-amber-400 mt-1">{item.conversion_reason}</p>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  </div>
);


const PRIORITY_COLORS = {
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
};
const EFFORT_COLORS = {
  'quick-win': 'bg-emerald-100 text-emerald-700',
  moderate: 'bg-blue-100 text-blue-700',
  major: 'bg-violet-100 text-violet-700',
};
const RISK_COLORS = {
  low: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  high: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
};
const CATEGORY_COLORS = {
  'Content & Keywords': 'text-indigo-600',
  'Technical SEO & Structure': 'text-cyan-600',
  'Trust & E-E-A-T': 'text-amber-600',
  'Technology Stack': 'text-blue-600',
  'Site Performance': 'text-emerald-600',
  'AI Search Readiness': 'text-violet-600',
  'Pricing & Feature Plans': 'text-pink-600',
};

const STATUS_CONFIG = {
  pending_approval:        { label: 'Pending Approval',     cls: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-400' },
  awaiting_final_approval: { label: 'Awaiting Approval',    cls: 'bg-orange-100 text-orange-700 border-orange-200 dark:bg-orange-900/30 dark:text-orange-400' },
  auto_applied:            { label: 'Auto-applied ✓',       cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
  manual_todo:             { label: 'In Queue',             cls: 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400' },
  manual_in_progress:      { label: 'In Progress',          cls: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-400' },
  manual_review:           { label: 'In Review',            cls: 'bg-violet-100 text-violet-700 border-violet-200 dark:bg-violet-900/30 dark:text-violet-400' },
  manual_done:             { label: 'Done ✓',               cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400' },
  rejected:                { label: 'Rejected',             cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400' },
  auto_skip_failed:        { label: 'Auto-skip Failed',     cls: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400' },
};
const TERMINAL_STATUSES = new Set(['rejected', 'auto_applied', 'manual_done']);

function ScoreBar({ label, myScore, compScore }) {
  const myColor = myScore >= compScore ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-slate-600';
  const compColor = compScore > myScore ? 'bg-red-400' : 'bg-slate-300 dark:bg-slate-600';
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-slate-600 dark:text-slate-300">{label}</span>
        <span className="text-slate-400">{myScore} vs {compScore}</span>
      </div>
      <div className="flex gap-1 h-2">
        <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${myColor} transition-all`} style={{ width: `${myScore}%` }} />
        </div>
        <div className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full ${compColor} transition-all`} style={{ width: `${compScore}%` }} />
        </div>
      </div>
      <div className="flex justify-between text-[10px] text-slate-400">
        <span>Your Site</span>
        <span>Competitor</span>
      </div>
    </div>
  );
}

const WebIntelResults = ({ data, selectedProjectId }) => {
  const navigate = useNavigate();
  const [expandedCat, setExpandedCat] = useState(null);
  const [replicating, setReplicating] = useState(null);
  const [replicateStatusByKey, setReplicateStatusByKey] = useState({});
  const [openStatusKey, setOpenStatusKey] = useState(null);

  const fetchReplicateStatus = () => {
    if (!selectedProjectId || !data?.competitor_url) return;
    api.get('/competitor/replicate/status', {
      params: { project_id: selectedProjectId, competitor_url: data.competitor_url },
    }).then(resp => {
      const seen = {};
      for (const row of (resp.data?.items || [])) {
        const k = row.title;
        if (!seen[k]) { seen[k] = row; continue; }
        if (seen[k].status === 'rejected' && row.status !== 'rejected') seen[k] = row;
      }
      setReplicateStatusByKey(seen);
    }).catch(() => {});
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchReplicateStatus(); }, [data?.competitor_url, selectedProjectId]);

  if (data.error && !data.categories?.length) {
    return (
      <Card>
        <CardContent className="p-8 text-center">
          <AlertTriangle className="h-8 w-8 text-amber-500 mx-auto mb-3" />
          <p className="text-slate-600">{data.error}</p>
        </CardContent>
      </Card>
    );
  }

  const gapScore = data.overall_gap_score ?? 50;
  const gapColor = gapScore >= 60 ? 'text-emerald-600' : gapScore >= 40 ? 'text-amber-600' : 'text-red-600';
  const gapLabel = gapScore >= 70 ? 'Ahead' : gapScore >= 50 ? 'Competitive' : gapScore >= 30 ? 'Behind' : 'Far Behind';

  return (
    <div className="space-y-6">
      {/* Section A: Score Overview */}
      <Card>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="flex flex-col items-center justify-center text-center">
              <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Overall Competitive Score</p>
              <div className="relative w-28 h-28">
                <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8" className="text-slate-100 dark:text-slate-800" />
                  <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="8"
                    className={gapColor}
                    strokeDasharray={`${gapScore * 2.64} 264`}
                    strokeLinecap="round" />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className={`text-2xl font-bold ${gapColor}`}>{gapScore}</span>
                  <span className="text-[10px] text-slate-500">{gapLabel}</span>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-3">
              <p className="text-sm text-slate-700 dark:text-slate-300">{data.summary}</p>
              <div className="space-y-2.5">
                {(data.categories || []).map((cat, i) => (
                  <ScoreBar key={i} label={cat.category} myScore={cat.my_site_score} compScore={cat.competitor_score} />
                ))}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Section B: Side-by-Side Comparison */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-indigo-500" /> Side-by-Side Comparison
          </CardTitle>
          <CardDescription>Click a category to expand findings</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {(data.categories || []).map((cat, i) => {
            const isOpen = expandedCat === i;
            const winner = cat.my_site_score >= cat.competitor_score ? 'you' : 'them';
            return (
              <div key={i} className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
                <button
                  onClick={() => setExpandedCat(isOpen ? null : i)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className={`font-medium ${CATEGORY_COLORS[cat.category] || 'text-slate-700'}`}>{cat.category}</span>
                    {winner === 'you' ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-[10px]">You Win</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 text-[10px]">Competitor Wins</Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-slate-400">{cat.my_site_score} vs {cat.competitor_score}</span>
                    {isOpen ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  </div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="overflow-hidden"
                    >
                      <div className="grid grid-cols-2 gap-4 p-4 pt-0">
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Your Site</p>
                          {(cat.my_site_findings || []).map((f, j) => (
                            <div key={j} className="flex items-start gap-2 text-sm">
                              <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700 dark:text-slate-300">{f}</span>
                            </div>
                          ))}
                          {(!cat.my_site_findings || cat.my_site_findings.length === 0) && (
                            <p className="text-xs text-slate-400 italic">No findings</p>
                          )}
                        </div>
                        <div className="space-y-2">
                          <p className="text-xs font-medium text-red-500 uppercase tracking-wider">Competitor</p>
                          {(cat.competitor_findings || []).map((f, j) => (
                            <div key={j} className="flex items-start gap-2 text-sm">
                              <ArrowUpRight className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                              <span className="text-slate-700 dark:text-slate-300">{f}</span>
                            </div>
                          ))}
                          {(!cat.competitor_findings || cat.competitor_findings.length === 0) && (
                            <p className="text-xs text-slate-400 italic">No findings</p>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Section C: Win Points */}
      {(data.win_points || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-amber-500" /> Win Points to Replicate
            </CardTitle>
            <CardDescription>Competitor advantages you can copy or surpass — sorted by priority</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(data.win_points || []).map((wp, i) => (
                <div key={i} className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 hover:border-indigo-200 dark:hover:border-indigo-800 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h4 className="font-semibold text-slate-900 dark:text-white">{wp.title}</h4>
                    <div className="flex gap-1.5 flex-shrink-0">
                      <Badge className={PRIORITY_COLORS[wp.priority] || PRIORITY_COLORS.medium}>
                        {wp.priority}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">{wp.category}</Badge>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div className="p-2.5 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900">
                      <p className="text-[10px] font-medium text-red-500 uppercase mb-1">Competitor Has</p>
                      <p className="text-sm text-red-800 dark:text-red-300">{wp.competitor_has}</p>
                    </div>
                    <div className="p-2.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-100 dark:border-amber-900">
                      <p className="text-[10px] font-medium text-amber-500 uppercase mb-1">You Lack</p>
                      <p className="text-sm text-amber-800 dark:text-amber-300">{wp.you_lack}</p>
                    </div>
                  </div>
                  <div className="p-2.5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-900 mb-2">
                    <p className="text-[10px] font-medium text-indigo-500 uppercase mb-1">Action to Take</p>
                    <p className="text-sm text-indigo-800 dark:text-indigo-300">{wp.action}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={EFFORT_COLORS[wp.effort] || EFFORT_COLORS.moderate}>{wp.effort}</Badge>
                    <Badge variant="outline" className="text-[10px]">Impact: {wp.impact}</Badge>
                    {wp.risk_level && (
                      <Badge className={RISK_COLORS[wp.risk_level] || RISK_COLORS.medium}>
                        Risk: {wp.risk_level}
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {wp.auto_applicable ? 'Auto-apply on approval' : 'Manual implementation'}
                    </Badge>
                    {(() => {
                      const row = replicateStatusByKey[wp.title];
                      if (!row) return null;
                      const cfg = STATUS_CONFIG[row.status] || { label: row.status, cls: 'bg-slate-100 text-slate-600 border-slate-200' };
                      return (
                        <button
                          onClick={() => setOpenStatusKey(openStatusKey === wp.title ? null : wp.title)}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[10px] font-medium cursor-pointer transition-opacity hover:opacity-80 ${cfg.cls}`}
                        >
                          <Clock className="h-2.5 w-2.5" /> {cfg.label}
                        </button>
                      );
                    })()}
                    <div className="ml-auto flex items-center gap-2">
                      <button
                        onClick={() => { navigator.clipboard.writeText(wp.action); toast.success('Action copied!'); }}
                        className="flex items-center gap-1 text-xs text-slate-400 hover:text-indigo-600 transition-colors"
                      >
                        <Copy className="h-3 w-3" /> Copy
                      </button>
                      {(() => {
                        const row = replicateStatusByKey[wp.title];
                        const isActive = row && !TERMINAL_STATUSES.has(row.status);
                        if (isActive) {
                          const dest = ['pending_approval', 'awaiting_final_approval'].includes(row.status)
                            ? '/dashboard/approvals'
                            : '/dashboard/tasks';
                          return (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-3 text-xs border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                              onClick={() => navigate(dest)}
                            >
                              <ExternalLink className="h-3 w-3 mr-1" /> View
                            </Button>
                          );
                        }
                        return (
                          <Button
                            size="sm"
                            className="h-7 px-3 text-xs bg-indigo-600 hover:bg-indigo-700 text-white"
                            disabled={replicating === i}
                            onClick={async () => {
                              setReplicating(i);
                              try {
                                const resp = await api.post('/competitor/replicate', {
                                  project_id: selectedProjectId || null,
                                  my_url: data.my_url || null,
                                  competitor_url: data.competitor_url || null,
                                  win_point: wp,
                                });
                                const now = new Date().toISOString();
                                setReplicateStatusByKey(prev => ({
                                  ...prev,
                                  [wp.title]: {
                                    title: wp.title,
                                    status: 'pending_approval',
                                    approval_id: resp.data?.approval_id,
                                    replicate_id: resp.data?.item_id,
                                    task_id: null,
                                    competitor_url: data.competitor_url,
                                    created_at: now,
                                    updated_at: now,
                                  },
                                }));
                                if (resp.data?.auto_applicable) {
                                  toast.success('Submitted for approval. It will auto-apply after final approval.');
                                } else {
                                  toast.success('Submitted for approval. It will be routed for manual implementation.');
                                }
                              } catch (err) {
                                console.error('Replicate error:', err);
                                toast.error(detailMessage(err.response?.data?.detail) || 'Failed to submit replicate request. Please try again.');
                              } finally {
                                setReplicating(null);
                              }
                            }}
                          >
                            {replicating === i ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Zap className="h-3 w-3 mr-1" />}
                            {replicating === i ? 'Submitting...' : 'Replicate'}
                          </Button>
                        );
                      })()}
                    </div>
                  </div>
                  {openStatusKey === wp.title && (() => {
                    const row = replicateStatusByKey[wp.title];
                    if (!row) return null;
                    const dest = ['pending_approval', 'awaiting_final_approval'].includes(row.status)
                      ? '/dashboard/approvals'
                      : row.task_id ? '/dashboard/tasks' : '/dashboard/approvals';
                    return (
                      <div className="mt-2 p-3 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-xs space-y-1.5">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium text-slate-700 dark:text-slate-300">Tracking Details</span>
                          <button
                            onClick={() => navigate(dest)}
                            className="flex items-center gap-1 text-indigo-600 hover:text-indigo-700 font-medium"
                          >
                            <ExternalLink className="h-3 w-3" /> Open
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-slate-500 dark:text-slate-400">
                          <span>Requested by</span>
                          <span className="text-slate-700 dark:text-slate-300">{row.requested_by_name || '—'}</span>
                          {row.assignee_name && (
                            <>
                              <span>Assigned to</span>
                              <span className="text-slate-700 dark:text-slate-300">{row.assignee_name}</span>
                            </>
                          )}
                          <span>Submitted</span>
                          <span className="text-slate-700 dark:text-slate-300">
                            {row.created_at ? new Date(row.created_at).toLocaleDateString() : '—'}
                          </span>
                          <span>Last updated</span>
                          <span className="text-slate-700 dark:text-slate-300">
                            {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}
                          </span>
                          {row.auto_skip_reason && (
                            <span className="col-span-2 text-amber-600 dark:text-amber-400 mt-0.5">{row.auto_skip_reason}</span>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Your Advantages */}
      {(data.your_advantages || []).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
              <Shield className="h-5 w-5" /> Your Advantages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(data.your_advantages || []).map((adv, i) => (
                <div key={i} className="flex items-start gap-2.5 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-900">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-emerald-800 dark:text-emerald-300">{adv}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};


export default CompetitorAnalysisPage;
